/**
 * usePatientInsuranceDocuments.js
 * Pedidos médicos e guias da pessoa (patients/insurance/documents/list.php) e as
 * operadoras com a exigência de pedido/guia (get_providers.php), buscados uma vez
 * pelo drawer de agendamento e repartidos entre os blocos "Pedido médico" e "Guia".
 *
 * `documents` só vem preenchido com os dados da pessoa atual (null = carregando);
 * `addDocument` coloca na lista um documento gravado na hora e `reload` busca de
 * novo (depois dos anexos, para trazer contadores e o "vencido" do backend).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { DataProviderInstance } from '../../../../../api/DataProvider';

const DOCUMENT_LISTS = { medical_request: 'requests', authorization: 'authorizations' };

export default function usePatientInsuranceDocuments(patientId, enabled = true) {
  const { GetPatientInsuranceDocuments, GetInsuranceProviders } = DataProviderInstance();
  const [documents, setDocuments] = useState(null);
  const [providerRules, setProviderRules] = useState([]);
  // Só a resposta da pessoa atual entra (troca rápida de pessoa)
  const currentPatient = useRef(null);
  currentPatient.current = enabled ? patientId : null;

  const reload = useCallback(() => {
    if (!enabled || patientId == null) return;
    GetPatientInsuranceDocuments(patientId, (result) => {
      if (currentPatient.current !== patientId) return;
      const data = result?.status === 'SUCCESS' ? (result.data || {}) : {};
      setDocuments({
        patientId,
        requests: Array.isArray(data.requests) ? data.requests : [],
        authorizations: Array.isArray(data.authorizations) ? data.authorizations : [],
      });
    });
  }, [patientId, enabled, GetPatientInsuranceDocuments]);

  useEffect(() => {
    setDocuments(null);
    reload();
  }, [patientId, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Exigência de pedido/guia por operadora (o CompanyConfig não traz)
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    GetInsuranceProviders((result) => {
      if (alive && result?.status === 'SUCCESS' && Array.isArray(result.data)) setProviderRules(result.data);
    });
    return () => { alive = false; };
  }, [enabled, GetInsuranceProviders]);

  /** Documento gravado agora ('medical_request' | 'authorization'), no topo da lista. */
  const addDocument = useCallback((type, doc) => {
    const key = DOCUMENT_LISTS[type];
    if (!key) return;
    setDocuments((prev) => {
      const base = prev && prev.patientId === patientId ? prev : { patientId, requests: [], authorizations: [] };
      return { ...base, [key]: [doc, ...base[key].filter((d) => String(d.id) !== String(doc.id))] };
    });
  }, [patientId]);

  return {
    documents: documents && documents.patientId === patientId ? documents : null,
    providerRules,
    reload,
    addDocument,
  };
}
